import { Typography } from '@mui/material'
import { styled as muiStyled } from '@mui/material/styles'
import I18n from 'i18n-js'
import moment from 'moment'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { makeStyles } from 'tss-react/mui'

import Sash from '@src/components/shared/Sash'
import useRetailerAccountWallet from '@src/components/wallet/useRetailerAccountWallet'
import { formatWithCents, numberFormatter } from '@src/lib/Utils'

import fonts from '../../../../lib/Font'
import SmartImage from '../../../shared/SmartImage'
import DefaultImage from '../../campaign-default.png'

const useStyles = makeStyles()((theme) => ({
  campaignImage: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
  // We need to separate the zoom effect and the image styles because SmartImage
  // imposes its own transition.
  zoomEffect: {
    'width': '100%',
    'height': '100%',
    'transform': 'scale(1)',
    'transition': 'transform 2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  imgWrapper: {
    height: '180px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  imgShadow: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    boxShadow: 'inset 0px 0px 15px 2px rgba(0,0,0,0.25)',
    zIndex: 1,
    pointerEvents: 'none',
  },
  root: {
    'backgroundColor': theme.palette.secondary.main,
    'width': '40px',
    'height': '40px',
    'clipPath': 'polygon(0 100%, 100% 0, 100% 100%, 0 100%)',
    '.MuiSvgIcon-root': {
      color: theme.palette.secondary.contrastText,
      position: 'absolute',
      bottom: '2px',
      right: '2px',
    },
  },
  funds: {
    position: 'absolute',
    right: theme.spacing(2),
    bottom: theme.spacing(2),
  },
}))

const Wrapper = styled.div`
  position: relative;
  font-family: ${fonts.family.lato};
  width: 100%;
`
const Logo = styled.div`
  height: 44px;
  flex: 0 0 96px;
  & img {
    background: white;
    /* border-radius: 4px; */
  }
`
const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`
const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Banner = styled.div`
  height: 180px;
  width: 100%;
  position: relative;
`

const CampaignCardCopy = styled.div`
  box-sizing: border-box;
  padding: 24px 16px 16px;
  width: 100%;
`

const Title = styled.h3`
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TimeRemaining = muiStyled('p')(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: '13px',
  height: '13px',
  margin: '4px 0',
}))

const Description = muiStyled('p')(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: '13px',
  lineHeight: '16px',
  height: '48px',
  overflow: 'hidden',
  marginBottom: 0,
}))

const Stats = muiStyled('ul')(({ theme }) => ({
  'listStyle': 'none',
  'padding': 0,
  'margin': 0,
  'display': 'flex',
  'justifyContent': 'center',
  'flexWrap': 'wrap',
  '& li': {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    margin: '0 16px 16px',
  },
  '& span:first-child': {
    fontSize: '2em',
    marginBottom: '0.5rem',
  },
  '& span:last-child': {
    color: theme.palette.grey[500],
    textTransform: 'uppercase',
  },
}))

const statNumberFormatter = numberFormatter()

const truncateDescriptionOnCharLimit = (desc) => {
  const characterLimit = 160
  return desc.length > characterLimit
    ? `${desc.substring(0, characterLimit)}...`
    : desc
}

const displayPremiumDate = (now, startDate, endDate) => {
  if (startDate.isAfter(now)) {
    return I18n.t('content.beginsOnDate', {
      date: startDate.format('L, h:mma'),
    })
  } else if (endDate.isBefore(now)) {
    return I18n.t('content.campaignEnded')
  } else {
    return I18n.t('content.endsOnDate', {
      date: endDate.format('L, h:mma'),
    })
  }
}

const formatDaysRemainingCopy = (type, now, startDate, endDate) => {
  let daysRemaining = endDate.diff(now, 'days')

  if (Number.isNaN(daysRemaining)) {
    return null
  }
  if (type === 'paidAd') {
    return displayPremiumDate(now, startDate, endDate)
  } else {
    return endDate.calendar(null, {
      sameDay: `[${I18n.t('content.endsToday')}] LT`,
      nextDay: `[${I18n.t('content.endsTomorrow')}] LT`,
      nextWeek: `[${I18n.t('content.xDaysRemaining', {
        count: daysRemaining,
      })},]`,
      sameElse: `[${I18n.t('content.xDaysRemaining', {
        count: daysRemaining,
      })},]`,
    })
  }
}

const CampaignCardBody = (props) => {
  const now = moment()
  const startDate = moment(props.startDate, moment.ISO_8601)
  const endDate = moment(props.endDate, moment.ISO_8601)
  const campaignStarted = startDate.isBefore(now)
  const { classes } = useStyles()
  const type = props.type
  let { views, actions, clicks } = props.stats || {}
  views = parseInt(views, 10)
  actions = parseInt(actions, 10)
  clicks = parseInt(clicks, 10)
  const { findLedgersApplicableToPaidAd } = useRetailerAccountWallet()

  return (
    <Wrapper>
      <Banner banner={props.thumbnailSrc}>
        <div className={classes.imgShadow} />
        <div className={classes.imgWrapper}>
          <div className={classes.zoomEffect}>
            <SmartImage
              alt={props.title}
              className={classes.campaignImage}
              fallbackSrc={DefaultImage}
              src={props.thumbnailSrc}
            />
          </div>
        </div>
        {props.sash && (
          <Sash
            label={I18n.t('shared.new')}
            backgroundColor="#7B26AF"
            tooltip={I18n.t('content.newSashTooltip')}
          />
        )}
      </Banner>
      <CampaignCardCopy>
        <TitleWrapper>
          <div
            style={{
              // min-width of 0 is needed for ellipsis to work in flex.
              // https://css-tricks.com/flexbox-truncated-text/
              minWidth: 0,
            }}
          >
            <Title>{props.title}</Title>
            {props.type !== 'contentDiscovery' && (
              <TimeRemaining>
                {formatDaysRemainingCopy(props.type, now, startDate, endDate)}
              </TimeRemaining>
            )}
          </div>
          <Logo>
            <StyledImage src={props.brandLogo} />
          </Logo>
        </TitleWrapper>

        <Description>
          {props.description
            ? truncateDescriptionOnCharLimit(props.description)
            : ''}
        </Description>
        {type === 'paidAd' && props.paidType === 'retailer_funded' && (
          <div className={classes.funds}>
            <Typography variant="subtitle2">
              {I18n.t('content.availableFundsWithAmount', {
                amount: formatWithCents.format(
                  findLedgersApplicableToPaidAd(props.brandUuid, {
                    excludeExpired: true,
                    excludeEmpty: true,
                  }).reduce((memo, x) => {
                    return memo + Number(x.balance)
                  }, 0) / 100,
                ),
              })}
            </Typography>
          </div>
        )}
      </CampaignCardCopy>
      {campaignStarted &&
        type === 'paidAd' &&
        (views > 0 || actions > 0 || clicks > 0) && (
          <Stats>
            <li>
              <span>
                {isNaN(views) ? '-' : statNumberFormatter.format(views)}
              </span>
              <span>{I18n.t('content.statsLabelViews')}</span>
            </li>
            <li>
              <span>
                {isNaN(actions) ? '-' : statNumberFormatter.format(actions)}
              </span>
              <span>{I18n.t('content.statsLabelActions')}</span>
            </li>
            <li>
              <span>
                {isNaN(clicks) ? '-' : statNumberFormatter.format(clicks)}
              </span>
              <span>{I18n.t('content.statsLabelClicks')}</span>
            </li>
          </Stats>
        )}
    </Wrapper>
  )
}

CampaignCardBody.propTypes = {
  brandLogo: PropTypes.string,
  description: PropTypes.string,
  endDate: PropTypes.string,
  sash: PropTypes.bool,
  startDate: PropTypes.string,
  stats: PropTypes.object,
  thumbnailSrc: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.oneOf(['paidAd', 'localAd', 'campaign', 'contentDiscovery']),
}

CampaignCardBody.defaultProps = {}

export default CampaignCardBody
